import React, { useEffect, useRef } from 'react'
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker'
import Button from 'components/button'
import { Triangle } from 'components/icons'

// In the manner of shadcn/ui's calendar, on react-day-picker: a month of 32px days under the month and year,
// each a dropdown, with arrows either side for the month before and after. Days are 13px; the week's day names
// are muted, and so are the days of the months either side. Today sits on the menu rows' hover fill and the day
// chosen is filled in the product's blue, as a ticked checkbox is.

type CalendarProps = React.ComponentProps<typeof DayPicker>

// The years the year list offers, either side of this one. With the month and year as lists, react-day-picker's
// own range would end at this year, so no later day could be picked.
const YEARS_BACK = 100
const YEARS_AHEAD = 50

// A day: the whole of its square is the button, round-cornered as a menu's rows are.
const CalendarDayButton = ({
  day,
  modifiers,
  className,
  ...props
}: React.ComponentProps<typeof DayButton>): React.JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null)
  // The keyboard moves through the days by moving which one is focused.
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      innerRef={ref}
      internalType="transparent"
      data-day={day.date.toLocaleDateString()}
      data-selected={modifiers.selected || undefined}
      data-today={modifiers.today || undefined}
      className={[
        'size-(--cell-size) p-0 rounded-md text-[13px] font-normal text-body tabular-nums',
        'hover:bg-surface-hover hover:text-body',
        'data-today:bg-surface-hover data-today:font-medium',
        'data-selected:bg-blue data-selected:text-white data-selected:font-medium data-selected:hover:bg-blue',
        'focus-visible:outline-offset-0',
        modifiers.outside && !modifiers.selected ? 'text-faint hover:text-muted' : '',
        className ?? ''
      ].join(' ')}
      {...(props as Omit<React.HTMLProps<HTMLButtonElement>, 'type'>)}
    />
  )
}

// The month before and after: the platform's quiet icon button, as the class names place it.
const MonthButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element => (
  <Button internalType="transparent" {...(props as Omit<React.HTMLProps<HTMLButtonElement>, 'type'>)} />
)

const Calendar: React.FC<CalendarProps> = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  startMonth,
  endMonth,
  formatters,
  components,
  ...props
}) => {
  const defaults = getDefaultClassNames()
  const thisYear = new Date().getFullYear()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      startMonth={startMonth ?? new Date(thisYear - YEARS_BACK, 0)}
      endMonth={endMonth ?? new Date(thisYear + YEARS_AHEAD, 11)}
      className={`w-fit select-none text-[13px] text-body [--cell-size:2rem] ${className ?? ''}`}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(undefined, { month: 'short' }),
        ...formatters
      }}
      classNames={{
        root: `${defaults.root} w-fit`,
        months: `${defaults.months} relative flex flex-col gap-4`,
        month: `${defaults.month} flex w-full flex-col gap-3`,
        nav: `${defaults.nav} absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1`,
        button_previous: `${defaults.button_previous} size-(--cell-size) p-0 rounded-md text-muted hover:bg-surface-hover hover:text-body aria-disabled:opacity-45`,
        button_next: `${defaults.button_next} size-(--cell-size) p-0 rounded-md text-muted hover:bg-surface-hover hover:text-body aria-disabled:opacity-45`,
        month_caption: `${defaults.month_caption} flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)`,
        dropdowns: `${defaults.dropdowns} flex h-(--cell-size) w-full items-center justify-center gap-1.5 font-medium`,
        // The month's and the year's own lists, invisible over their labels, so a click on either opens its list.
        dropdown_root: `${defaults.dropdown_root} relative rounded-md has-focus-visible:ring-3 has-focus-visible:ring-blue/25`,
        dropdown: `${defaults.dropdown} absolute inset-0 cursor-pointer opacity-0`,
        caption_label: `${defaults.caption_label} flex h-7 items-center gap-1 rounded-md pl-2 pr-1 font-medium hover:bg-surface-hover [&>svg]:size-3.5 [&>svg]:text-muted`,
        month_grid: 'w-full border-collapse',
        weekdays: `${defaults.weekdays} flex`,
        weekday: `${defaults.weekday} flex-1 text-[12px] font-normal text-muted`,
        week: `${defaults.week} mt-1 flex w-full`,
        day: `${defaults.day} relative p-0 text-center`,
        today: defaults.today,
        outside: defaults.outside,
        disabled: `${defaults.disabled} opacity-45`,
        hidden: `${defaults.hidden} invisible`,
        ...classNames
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) => (
          <Triangle
            type={orientation === 'left' ? 'left' : orientation === 'right' ? 'right' : 'down'}
            className={chevronClassName}
          />
        ),
        DayButton: CalendarDayButton,
        PreviousMonthButton: MonthButton,
        NextMonthButton: MonthButton,
        ...components
      }}
      {...props}
    />
  )
}

export default Calendar
